import getSelectedFolderView from '../selectors/getSelectedFolderView';
import { PerformanceDatapoint } from 'owa-analytics';
import { PerformanceDatapointCheckmarks } from '../datapoints';
import {
    toTodo,
    getRecentlyCompletedTasksService,
    getTasksService,
    GetTasksResponse,
} from 'owa-todo-service';
import { withCurrentRequestCheck, poll, cancelPoll } from 'owa-request-manager';
import {
    addRecentlyCompletedTodosToFolderView,
    addTodosToFolderView,
} from '../actions/todoFolderViewActions';
import {
    GET_RECENTLY_COMPLETED_TASKS_SERVICE_IDENTIFIER,
    GET_TASKS_IN_FOLDER_SERVICE_IDENTIFIER,
    POLLING_INTERVAL,
} from './constants';

const MAX_RECENTLY_COMPLETED_TASKS = 5;

export async function reloadItems(perfDatapoint?: PerformanceDatapoint) {
    await reloadActiveTaskItems(perfDatapoint);
    await reloadCompletedTaskItems(perfDatapoint);
}

export async function reloadActiveTaskItems(perfDatapoint?: PerformanceDatapoint) {
    // Remember the folder id we are requesting for so we can match against selected folder
    // when the call returns.
    const folderView = getSelectedFolderView();
    const response = await withCurrentRequestCheck(
        () => getTasksService(folderView.folderId),
        GET_TASKS_IN_FOLDER_SERVICE_IDENTIFIER
    );

    if (perfDatapoint) {
        perfDatapoint.addCheckmark(PerformanceDatapointCheckmarks.fetchedActiveTasks);
    }

    if (response?.isCurrent) {
        activeTaskItemsResponseReceived(response.response, perfDatapoint);
    }
}

export function activeTaskItemsResponseReceived(
    response: GetTasksResponse,
    perfDatapoint?: PerformanceDatapoint
) {
    if (response && !response.errorResponse) {
        // Empty the folders first.
        addTodosToFolderView(null, true /* deleteAll */);

        // Now add new todos.
        response.Value.forEach(task => {
            let todoItem = toTodo(task);
            addTodosToFolderView([todoItem]);
        });

        if (perfDatapoint) {
            perfDatapoint.addCheckmark(PerformanceDatapointCheckmarks.processedActiveTasks);
        }
    }
}

export async function reloadCompletedTaskItems(perfDatapoint?: PerformanceDatapoint) {
    // Remember the folder id we are requesting for so we can match against selected folder
    // when the call returns.
    const folderView = getSelectedFolderView();
    const response = await withCurrentRequestCheck(
        () => getRecentlyCompletedTasksService(MAX_RECENTLY_COMPLETED_TASKS, folderView.folderId),
        GET_RECENTLY_COMPLETED_TASKS_SERVICE_IDENTIFIER
    );

    if (perfDatapoint) {
        perfDatapoint.addCheckmark(PerformanceDatapointCheckmarks.fetchedCompletedTasks);
    }

    if (response?.isCurrent) {
        completedTaskItemsResponseReceived(response.response, perfDatapoint);
    }
}

export function completedTaskItemsResponseReceived(
    response: GetTasksResponse,
    perfDatapoint?: PerformanceDatapoint
) {
    if (response && !response.errorResponse) {
        // Empty the folders first.
        addRecentlyCompletedTodosToFolderView(null, true /* deleteAll */);

        // Now add new todos.
        response.Value.forEach(task => {
            let todoItem = toTodo(task);
            addRecentlyCompletedTodosToFolderView([todoItem]);
        });

        if (perfDatapoint) {
            perfDatapoint.addCheckmark(PerformanceDatapointCheckmarks.processedCompletedTasks);
        }
    }
}

export function pollTasks() {
    poll(
        () => {
            const folderView = getSelectedFolderView();
            return getTasksService(folderView.folderId);
        },
        GET_TASKS_IN_FOLDER_SERVICE_IDENTIFIER,
        POLLING_INTERVAL
    );
    poll(
        () => {
            const folderView = getSelectedFolderView();
            return getRecentlyCompletedTasksService(
                MAX_RECENTLY_COMPLETED_TASKS,
                folderView.folderId
            );
        },
        GET_RECENTLY_COMPLETED_TASKS_SERVICE_IDENTIFIER,
        POLLING_INTERVAL
    );
}

export function cancelPollTasks() {
    cancelPoll(GET_TASKS_IN_FOLDER_SERVICE_IDENTIFIER);
    cancelPoll(GET_RECENTLY_COMPLETED_TASKS_SERVICE_IDENTIFIER);
}
